import React, { useState, useEffect } from 'react';
import './List.scss';
import PaginationController from "../Pagination";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { Popover } from 'react-tiny-popover';
import HeaderSort from './HeaderSort';
import OpenFilter from './OpenFilter';
import HeaderFilter from './HeaderFilter';
import ItemCell from './ItemCell';
import SuspenseItemCell from './SuspenseItemCell';

function List(props) {
	const {
		header,
		action,
		pagination,
		className,
		onKeyPress,
		disableKeyboardControls,
	} = props;

	const {
		itemsPerPage,
		page,
		setPage,
		leading,
		trailing,
		around
	} = pagination ?? {};

	const [sortKey, setSortKey] = useState(null);
	const [sortDirection, setSortDirection] = useState(null);
	const [filters, setFilters] = useState({});
	const [openFilter, setOpenFilter] = useState(null);
	const [selectedItem, setSelectedItem] = useState();

	const paginated = !!itemsPerPage;

	// TODO: Other keypress handlers could come from custom props.onKeyPress method
	useEffect(() => {
		console.log('list check disableKeyboardControls', disableKeyboardControls);
		if (disableKeyboardControls) return;

		// detect keypresses
		const keypresser = (e) => {
			switch (e.keyCode) {
				// Escape key
				case 27:
					setSelectedItem(undefined);
					action?.(e, undefined)
					break;
				// Key up || Key down
				case 38:
				case 40:
					const currentRowIndex = content.findIndex(contentRow => JSON.stringify(contentRow) === JSON.stringify(selectedItem))
					const adder = e.keyCode === 38 ? -1 : e.keyCode === 40 ? 1 : 0;
					const newRow = content[currentRowIndex + adder];
					if (newRow) {
						setSelectedItem(newRow);
						action?.(e, newRow)
					}
					break
				
				// // Key shift + D
				// case 68:
				// 	if (e.shiftKey) {
				// 		console.log('DOEN');
				// 	} 
				// 	break;

				// // Key shift + L
				// case 76:
				// 	if (e.shiftKey) {
				// 		console.log('LATER');
				// 	} 
				// 	break;

				// // Key shift + G
				// case 71:
				// 	if (e.shiftKey) {
				// 		console.log('GEEN');
				// 	} 
				// 	break;

				// // Key shift + S
				// case 83:
				// 	if (e.shiftKey) {
				// 		console.log('SERVICE');
				// 	} 
				// 	break;
				default:
					if (selectedItem && onKeyPress) {
						onKeyPress(e.keyCode, selectedItem);
					}
			}
		};

		if (selectedItem) {
			window.addEventListener('keydown', keypresser);
		}

		return () => {
			window.removeEventListener('keydown', keypresser);
		}
	}, [selectedItem]);

	const gridTemplateColumns = props.gridTemplateColumns ?? `repeat(${header.length}, 1fr)`;

	const isFilterActive = (filter, key) => {
		if (filter?.isFilterActive) return filter.isFilterActive(filters?.[key]);
		return filters?.[key];
	};

	const getContent = () => {
		let content = [ ...props.content ?? []];

		// Filtering
		header.forEach(column => {
			const filterValue = filters?.[column.key]
			if (isFilterActive(column.filter, column.key)) content = content.filter(item => column.filter?.test(item, filterValue));
		});

		// Sorting
		if (sortKey && sortDirection) content = content.sort(header.find(h => h.key === sortKey)?.sort);
		if (sortDirection === 'descending') content = content.reverse();

		return content;
	};

	const handleSortClick = (key) => {
		if (sortKey !== key) {
			setSortKey(key);
			setSortDirection('ascending');
			return;
		}

		if (sortDirection === 'ascending') return setSortDirection('descending');
		if (sortDirection === 'descending') {
			setSortKey(null);
			setSortDirection(null);
			return;
		}
		setSortDirection('ascending');
	};

	const renderHeaderColumn = ({ renderHeader, key, title, sort, filter }) => (
		<div key={key} className={`header__column ${key} ${sort ? 'sortable' : ''}`}>
			<div className='header__content' onClick={() => { if (sort) handleSortClick(key) }}>
				<div className='left'>
					{renderHeader ? renderHeader({ title, key }) : title}
				</div>
				<div className='right'>
          {filter && <HeaderFilter
            filterKey={key}
            filter={filter}
            filters={filters}
            setFilters={setFilters}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            isFilterActive={isFilterActive}
          />}
          {sort && <HeaderSort
            filterKey={key}
            sortKey={sortKey}
            sortDirection={sortDirection}
            handleSortClick={handleSortClick}
             />}
				</div>
			</div>
		</div>
	);

  	const renderSuspenseItemCell = (column) => (
		<span key={column.key} className={`item__cell ${column.key} suspense`} />
	);

	const onRowClick = (e, row) => {
		setSelectedItem(prevRow => JSON.stringify(prevRow) === JSON.stringify(row) ? undefined : row);
		if (action) {
			action(e, row)
		}
	}

	useEffect(() => {
		if (!selectedItem) return;

		if (props.content?.findIndex(item => JSON.stringify(item) === JSON.stringify(selectedItem[0])) === -1) {
			onRowClick(undefined, undefined)
		}
	}, [props.content]);


	const renderListItem = (listItem, index) => {
		if (
			paginated &&
			(index < (page * itemsPerPage)) ||
			(index >= ((page + 1) * itemsPerPage))
		) return null;

		const highlightClassnames = props.highlight?.filter(({ filter }) => filter(listItem)).map(({ className }) => className).join(' ') ?? '';

		return (
			<div
				key={listItem.id ?? index}
				className={`list__item list__item--action ${highlightClassnames} ${JSON.stringify(selectedItem) === JSON.stringify(listItem) ? 'selected' : ''}`}
				onClick={(e) => onRowClick(e, listItem)}
			>
        {header.map((column) => <ItemCell key={column.key} column={column} listItem={ listItem} />)}
			</div>
		);
	};

	const renderSuspenseListItem = (_, index) => (
		<div
			key={index}
			className={'list__item'}
		>
			{header.map((column, i) => <SuspenseItemCell key={`hcol-${ i}`} column={column} />)}
		</div>
	);

	const content = getContent();

	return (
		<div className={'List' + (className ? ' ' + className : '')} >
			<div className={'list__wrapper'} style={{ ['--columns']: gridTemplateColumns } as React.CSSProperties}>
				<div className={'list__header'} >
					{header.map(renderHeaderColumn)}
				</div>
				<div className={'list__body'}>
					{content.map(renderListItem)}
					{!props.content && [ ...Array(itemsPerPage ?? 12)].map(renderSuspenseListItem)}
				</div>
			</div>

			{paginated && content?.length > itemsPerPage && (
				<PaginationController
					page={page}
					setPage={setPage}
					pages={Math.ceil(content.length / itemsPerPage)}
					leading={leading ?? 1}
					trailing={trailing ?? 1}
					around={around ?? 2}
				/>
			)}
		</div>
	);
}

export default List;
